import { t } from "i18next";
import type { JSX } from "react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import ConfirmDialog from "@/components/ui/ConfirmDialog";

export const VisitSubmissionNotice = ({
  error,
  recovery,
  disabled,
  onAbandon,
  recording = false,
}: {
  error: string | null;
  recovery: boolean;
  disabled: boolean;
  onAbandon: () => void;
  recording?: boolean;
}): JSX.Element | null => {
  const [confirm, setConfirm] = useState(false);
  if (!error && !recovery) return null;
  return (
    <div
      className="flex flex-col gap-3 px-6 py-3"
      data-testid="visit-submission-notice"
      role="alert"
    >
      <p className="text-sm leading-5">
        {error ??
          t(
            "visits.submission.uncertain",
            "A previous submission is unresolved. Use the retry action if available, or retry with unchanged input. Check Visits before starting again.",
          )}
      </p>
      {/* Stacked on phones, side by side when they fit; each a 44px target. */}
      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-4">
        <a
          className="inline-flex min-h-11 items-center self-start rounded-md text-sm font-medium underline underline-offset-4 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
          href="/visits"
          rel="noreferrer"
          target="_blank"
        >
          {t("visits.submission.check", "Check Visits")}
        </a>
        {recovery && (
          <ConfirmDialog
            open={confirm}
            title={t(
              "visits.submission.abandon",
              "Abandon pending submission?",
            )}
            description={
              recording
                ? t(
                    "visits.submission.recordingAbandonDescription",
                    "Check the visit for the saved recording first. Abandoning removes this retry identity; submitting again could create and charge another recording.",
                  )
                : t(
                    "visits.submission.abandonDescription",
                    "Check Visits for the saved note first. Abandoning removes this retry identity; submitting again could create and charge another visit.",
                  )
            }
            trigger={
              <Button
                className="h-auto min-h-11 w-full whitespace-normal sm:w-auto"
                disabled={disabled}
                variant="outline"
              >
                {t("visits.submission.abandon", "Abandon pending submission?")}
              </Button>
            }
            onConfirm={onAbandon}
            onOpenChange={setConfirm}
          />
        )}
      </div>
    </div>
  );
};
